import { createContext, useContext } from 'react';
import { NotificationInstance } from 'antd/es/notification/interface';
import type { NotificationProviderProps } from './types';
import { useNotificationProvider } from './useNotificationProvider';

const NotificationContext = createContext<NotificationInstance | null>(null);

/**
 * Exposes the app-wide notification API to components. Reads NotificationContext and throws when the hook is used outside NotificationProvider.
 *
 * @returns Notification context value provided by NotificationProvider.
 */
const useGlobalNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useGlobalNotification must be used within NotificationProvider');
  }
  return context;
};

/**
 * Makes the Ant Design notification API available to the whole component tree. Publishes notificationApi from useNotificationProvider through NotificationContext and renders its context holder alongside the children.
 *
 * @param children - The subtree that gains access to the notification API.
 * @returns The context provider element wrapping the children.
 */
const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { notificationApi, contextHolder } = useNotificationProvider();

  return (
    <NotificationContext.Provider value={notificationApi}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export { NotificationContext, NotificationProvider, useGlobalNotification };
